/**
 * The stages that can be mid-flight while the buyer sits in the wizard, and
 * what Robbie says while each one runs.
 *
 * Honest UI: a stage that is working leaves the buyer nothing to act on, so
 * the docked panel drops its next-best-step suggestion — an inert suggestion
 * is a control that does nothing — and shows the matching line instead. The
 * flags come from the steps themselves (the same ones their loading screens
 * already render from); this module only names the states and owns the copy,
 * so the wording lives in one place.
 */
export type StageActivity =
  | 'bom-extraction'
  | 'criteria'
  | 'bom-analysis'
  | 'research'
  | 'recommendation'
  | 'credits-check'
  | 'bom-validation'
  | 'research-start';

/**
 * A stage plus the words the step's own overlay uses for it ("Going through
 * your 9 components."), so the panel and the page never describe one wait
 * two ways. A bare stage falls back to the stage's default line.
 */
export interface StageWork {
  stage: StageActivity;
  text: string;
  /** The wait failed; `text` is the CompactError's own title. */
  failed?: boolean;
}

export type StageReport = StageActivity | StageWork;

const STATUS: Record<StageActivity, string> = {
  'bom-extraction':
    "I'm reading your documents and pulling out the components — nothing needed from you right now.",
  criteria: "I'm saving your criteria and starting the analysis — nothing needed from you right now.",
  'bom-analysis': "I'm analyzing your BOM — nothing needed from you right now.",
  research: 'Research is running — nothing needed from you right now.',
  recommendation: "I'm working out the award recommendation — nothing needed from you right now.",
  'credits-check': 'Checking your balance.',
  'bom-validation': "I'm checking your BOM — nothing needed from you right now.",
  'research-start': 'Starting supplier research.',
};

export const stageOfReport = (report: StageReport | null): StageActivity | null =>
  report == null ? null : typeof report === 'string' ? report : report.stage;

/** A failed wait, reported in the same words as the step's CompactError. */
export const failedStage = (stage: StageActivity, title: string): StageWork => ({ stage, text: title, failed: true });

export const stageActivityFailed = (report: StageReport | null): boolean =>
  report != null && typeof report !== 'string' && Boolean(report.failed);

export const stageActivityStatus = (report: StageReport | null): string | undefined => {
  if (report == null) return undefined;
  return typeof report === 'string' ? STATUS[report] : report.text;
};
